import { useCallback } from 'react';
import { useLocale } from '@hooks/useLocale';
import { HookError } from '@adapters/bigcommerce/errorHandlers';
import {
  MecProduct,
  QuickAddProduct,
} from '@adapters/bigcommerce/types/product';

const fetchProductByCode = async <T>(
  locale: string,
  code: string,
  query = ''
): Promise<T | null> => {
  try {
    const response = await fetch(`/api/product/${code}${query}`, {
      method: 'GET',
      headers: {
        'Accept-Language': locale,
      },
    });

    if (response.status === 200) {
      const { data } = await response.json();
      return { ...data, code };
    }
    return null;
  } catch (error) {
    console.error(`Error retrieving product code: ${code} error: ${error}`);
    return null;
  }
};

export const productFetcher = (
  locale: string,
  code: string
): Promise<MecProduct | null> => fetchProductByCode<MecProduct>(locale, code);

/**
 * Slim product projection for Quick Add: colours/sizes/variants and the
 * scalars needed to add to cart — no images, videos or breadcrumbs.
 */
export const quickAddProductFetcher = (
  locale: string,
  code: string
): Promise<QuickAddProduct | null> =>
  // `locale` is duplicated into the query string — the server still resolves
  // it from `Accept-Language` — so the shared edge cache keyed on this URL is
  // per-locale even where the CDN doesn't honour `Vary: Accept-Language`. See
  // the quick-add cache headers in `adapters/bigcommerce/api/getProduct.ts`.
  fetchProductByCode<QuickAddProduct>(
    locale,
    code,
    `?view=quick-add&locale=${encodeURIComponent(locale)}`
  );

const useProduct = () => {
  const locale = useLocale();
  if (!locale)
    throw new HookError('Locale must be specified to retrieve a product');

  const getProduct = useCallback(
    (code: string) => productFetcher(locale, code),
    [locale]
  );

  return { getProduct };
};

export default useProduct;
